import React, { useState, useEffect } from 'react';
import { Link, RefreshCw, Trash2, Building2 } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import AccountSelectionScreen from './AccountSelectionScreen';
import OnboardingSuccess from './OnboardingSuccess';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function LinkGoogleAdsAccount({ onAccountLinked }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [showSelection, setShowSelection] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedAccountCount, setSelectedAccountCount] = useState(0);
  const [currentStep, setCurrentStep] = useState(2); // Step 2: account selection

  useEffect(() => {
    fetchConnectedAccounts();
    
    // Check if we just returned from OAuth
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('oauth') === 'success') {
      const accountCount = parseInt(urlParams.get('accounts') || '0');
      console.log(`✅ OAuth successful, ${accountCount} accounts found`);
      
      // Clear URL params
      window.history.replaceState({}, '', '/settings');
      
      handlePostOAuth(accountCount);
    }
  }, []);

  const handlePostOAuth = async (accountCount) => {
    // Always show selection screen so user explicitly picks accounts
    // This prevents the "full MCC linked" bug
    if (accountCount > 0) {
      console.log(`📋 Showing account selection for ${accountCount} accounts`);
      setCurrentStep(2);
      setShowSelection(true);
    } else {
      console.error('❌ No accounts found after OAuth');
    }
  };

  const autoSelectAllAccounts = async () => {
    try {
      const token = await getToken();
      
      // Fetch all available accounts
      const response = await fetch(`${API_URL}/api/auth/google/connections/available`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.connections) {
        // Only select client accounts, not Manager (MCC) accounts
        const clientAccounts = data.connections.filter(conn => conn.account_type !== 'Manager');
        const clientIds = clientAccounts.map(conn => conn.id);
        
        await fetch(`${API_URL}/api/auth/google/connections/select`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ selectedAccountIds: clientIds })
        });
        
        // Sync localStorage with customer_ids
        const customerIds = clientAccounts.map(conn => conn.customer_id);
        localStorage.setItem('linkedGoogleAdsAccounts', JSON.stringify(customerIds));
        
        console.log(`✅ Auto-selected ${clientIds.length} client account(s) (excluded ${data.connections.length - clientIds.length} MCC accounts)`);
        
        // Show success screen
        setSelectedAccountCount(clientIds.length);
        setShowSuccess(true);
        setCurrentStep(3);
      }
    } catch (err) {
      console.error('Error auto-selecting accounts:', err);
      setError('Failed to configure accounts. Please try again.');
    }
  };

  const handleSelectionComplete = (count) => {
    console.log(`✅ Account selection completed: ${count} account(s)`);
    // Show success screen (step 3)
    setSelectedAccountCount(count);
    setShowSelection(false);
    setShowSuccess(true);
    setCurrentStep(3);
  };

  const handleSuccessContinue = () => {
    // Clear old localStorage linkedAccounts so auto-populate kicks in
    localStorage.removeItem('linkedGoogleAdsAccounts');
    
    // Trigger account refresh
    if (onAccountLinked) {
      onAccountLinked();
    }
    
    // Redirect to dashboard with onboarding flag
    window.location.href = '/?onboarding=complete';
  };

  const fetchConnectedAccounts = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/auth/google/connections`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setConnectedAccounts(data.connections || []);
      }
    } catch (err) {
      console.error('Error fetching connected accounts:', err);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleLinkAccount = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/auth/google/start`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.url) {
        // Open the OAuth URL in same window (simpler UX)
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to start OAuth flow');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error starting OAuth:', err);
      setError('Failed to start OAuth. Please try again.');
      setLoading(false);
    }
  };

  const handleDisconnectAccount = async (connectionId) => {
    if (!window.confirm('Are you sure you want to disconnect this Google Ads account?')) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/auth/google/connections/${connectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchConnectedAccounts();
        if (onAccountLinked) {
          onAccountLinked();
        }
      }
    } catch (err) {
      console.error('Error disconnecting account:', err);
      alert('Failed to disconnect account. Please try again.');
    }
  };

  const handleSelectionCompleteOld = () => {
    setShowSelection(false);
    // Refresh connected accounts
    fetchConnectedAccounts();
    
    // Trigger account-linked event for other components
    if (onAccountLinked) {
      onAccountLinked();
    }
  };

  if (loadingAccounts) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem'
      }}>
        <RefreshCw size={32} style={{ color: '#6366f1', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Show success screen
  if (showSuccess) {
    return (
      <OnboardingSuccess 
        accountCount={selectedAccountCount}
        onContinue={handleSuccessContinue}
      />
    );
  }

  return (
    <div>
      {/* Account Selection Modal */}
      {showSelection && (
        <AccountSelectionScreen 
          onComplete={handleSelectionComplete}
          currentStep={currentStep}
        />
      )}
      {/* Header with Link Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'white', margin: 0, marginBottom: '8px' }}>
            Connected Google Ads Accounts
          </h2>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
            Link your Google Ads Manager (MCC) or individual accounts
          </p>
        </div>
        
        <button
          onClick={handleLinkAccount}
          disabled={loading}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)',
            opacity: loading ? 0.6 : 1
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.6)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(99, 102, 241, 0.4)';
            }
          }}
        >
          {loading ? (
            <>
              <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Connecting...
            </>
          ) : (
            <>
              <Link size={18} />
              Link New Account
            </>
          )}
        </button>
      </div>

      {error && (
        <div style={{
          padding: '16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          marginBottom: '24px',
          color: '#ef4444'
        }}>
          {error}
        </div>
      )}

      {/* Connected Accounts List */}
      {connectedAccounts.length === 0 ? (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          padding: '60px',
          textAlign: 'center',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Building2 size={48} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '1.25rem', color: 'white', marginBottom: '8px' }}>
            No Google Ads Accounts Connected
          </h3>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>
            Link your first account to start managing your campaigns
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {connectedAccounts.map(account => (
            <div
              key={account.id}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Building2 size={24} style={{ color: 'white' }} />
                </div>
                
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: 'white' }}>
                      {account.account_name || `Customer ${account.customer_id}`}
                    </span>
                    {account.account_type === 'Manager' && (
                      <span style={{
                        padding: '2px 8px',
                        background: 'rgba(139, 92, 246, 0.2)',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                        color: '#a78bfa',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        borderRadius: '4px'
                      }}>
                        MCC
                      </span>
                    )}
                    {account.is_primary && (
                      <span style={{
                        padding: '2px 8px',
                        background: 'rgba(16, 185, 129, 0.2)',
                        color: '#10b981',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        borderRadius: '4px'
                      }}>
                        PRIMARY
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                    Customer ID: {account.customer_id}
                    {account.account_type === 'Manager' && ' • Manager Account'}
                    {account.created_at && ` • Connected ${new Date(account.created_at).toLocaleDateString()}`}
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleDisconnectAccount(account.id)}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                }}
              >
                <Trash2 size={16} />
                Disconnect
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
